import { injectable } from 'tsyringe';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';

/** Which lens on the audit trail to read — a tenant's own rows, or all tenants. */
export type AuditScope = 'tenant' | 'operator';

export interface AuditLogEntry {
    id: string;
    tenantId: string;
    actorId: string;
    action: string;
    resourceType: string;
    resourceId: string;
    summary: string;
    previousState: Record<string, unknown> | null;
    newState: Record<string, unknown> | null;
    correlationId: string | null;
    createdAt: string;
}

export interface AuditLogPage {
    entries: AuditLogEntry[];
    total: number;
    limit: number;
    offset: number;
}

/** Server-side filters; empty fields are omitted from the query string. */
export interface AuditLogFilters {
    actor?: string;
    action?: string;
    resourceType?: string;
    from?: string;
    to?: string;
    tenantId?: string;
    limit: number;
    offset: number;
}

function buildQueryString(filters: AuditLogFilters): string {
    const params = new URLSearchParams();
    if (filters.actor) params.set('actor', filters.actor);
    if (filters.action) params.set('action', filters.action);
    if (filters.resourceType) params.set('resourceType', filters.resourceType);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.tenantId) params.set('tenantId', filters.tenantId);
    params.set('limit', String(filters.limit));
    params.set('offset', String(filters.offset));
    return params.toString();
}

/**
 * Reads the audit trail (SAAS-29) from bmai-auth over the BFF. The `tenant` scope
 * hits the `settings.view`-gated `/api/audit-log` (forced to the caller's own
 * tenant server-side); the `operator` scope hits the platform-operator-gated
 * `/api/operator/audit-log` (cross-tenant, feeds the SAAS-34 platform view).
 */
@injectable()
export class AuditLogService {
    async list(scope: AuditScope, filters: AuditLogFilters): Promise<AuditLogPage> {
        const base = scope === 'operator' ? '/_auth/api/operator/audit-log' : '/_auth/api/audit-log';
        const res = await authenticatedFetch(`${base}?${buildQueryString(filters)}`);
        if (!res.ok) {
            let message = `Failed to load audit log (${res.status})`;
            try {
                const body = (await res.json()) as { error?: string };
                if (body.error) message = body.error;
            } catch { void 0; }
            throw new Error(message);
        }
        return (await res.json()) as AuditLogPage;
    }
}
